import React, { useEffect, useRef, useState } from 'react';

// Most photos on the site are 3:4 portraits, so that's the placeholder shape
// until the real size is known.
const DEFAULT_RATIO = 3 / 4;

// Start loading a photo when it's about one phone screen away. The browser's own
// loading="lazy" starts ~1250px out, which on a phone grid is most of an album.
const LOAD_AHEAD = '800px 0px';

/**
 * A responsive photo that fades in once loaded over a shimmering placeholder.
 *
 * `cover`: fills its positioned parent, cropped (cards). Otherwise the photo keeps
 * its own proportions and reserves that space before it loads (grids).
 * `priority`: for photos visible on first paint; loads eagerly and first.
 */
function Photo({ src, srcSet, sizes, alt, cover = false, priority = false, className = '' }) {
  const imgRef = useRef(null);
  const [loaded, setLoaded] = useState(false);
  const [near, setNear] = useState(priority);
  const [ratio, setRatio] = useState(DEFAULT_RATIO);

  useEffect(() => {
    if (near) return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setNear(true);
          observer.disconnect();
        }
      },
      { rootMargin: LOAD_AHEAD }
    );
    observer.observe(imgRef.current);
    return () => observer.disconnect();
  }, [near]);

  const handleLoad = () => {
    const img = imgRef.current;
    if (img?.naturalWidth) setRatio(img.naturalWidth / img.naturalHeight);
    setLoaded(true);
  };

  // A photo from the browser cache can finish before React attaches onLoad.
  useEffect(() => {
    if (imgRef.current?.complete && imgRef.current.naturalWidth) handleLoad();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const img = (
    <img
      ref={imgRef}
      src={near ? src : undefined}
      srcSet={near && srcSet ? srcSet : undefined}
      sizes={near && srcSet ? sizes : undefined}
      alt={alt}
      fetchpriority={priority ? 'high' : undefined}
      decoding="async"
      onLoad={handleLoad}
      className={`absolute inset-0 h-full w-full ${cover ? 'object-cover' : 'object-contain'} transition-opacity duration-500 ${loaded ? 'opacity-100' : 'opacity-0'} ${className}`}
    />
  );

  if (cover) {
    return (
      <>
        {!loaded && <div className="shimmer absolute inset-0" aria-hidden="true" />}
        {img}
      </>
    );
  }

  return (
    <div className={`relative w-full ${loaded ? '' : 'shimmer'}`} style={{ aspectRatio: ratio }}>
      {img}
    </div>
  );
}

export default Photo;
